import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface QuizProgressChartProps {
  data: Array<{
    quiz_date: string;
    accuracy: string;
  }> | null;
}


const QuizProgressChart: React.FC<QuizProgressChartProps> = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="card border-0 shadow-sm" style={{ borderRadius: '15px' }}>
        <div className="card-header bg-transparent border-0 pt-4">
          <h5 className="card-title fw-bold mb-0">
            <i className="fas fa-chart-line me-2 text-primary"></i>
            Quiz Progress Over Time
          </h5>
          <small className="text-muted">Track your accuracy improvement across recent quizzes</small>
        </div>
        <div className="card-body text-center text-muted p-4">
          <i className="fas fa-chart-line fa-2x mb-2"></i>
          <p>No quiz progress data available</p>
        </div>
      </div>
    );
  }

  // Sort data by date
  const sortedData = [...data].sort((a, b) => new Date(a.quiz_date).getTime() - new Date(b.quiz_date).getTime());

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const chartData = {
    labels: sortedData.map(item => formatDate(item.quiz_date)),
    datasets: [
      {
        label: 'Quiz Accuracy (%)',
        data: sortedData.map(item => parseFloat(item.accuracy)),
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        borderWidth: 3,
        pointRadius: 6,
        pointHoverRadius: 8,
        pointBackgroundColor: 'rgb(75, 192, 192)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        fill: true,
        tension: 0.4, // Smooth curve
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          font: {
            size: 12,
            weight: 'bold' as const,
          },
          color: '#333',
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: 'rgb(75, 192, 192)',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          label: function(context: any) {
            return `Accuracy: ${context.parsed.y.toFixed(1)}%`;
          },
          title: function(context: any) {
            const dataIndex = context[0].dataIndex;
            return `Date: ${sortedData[dataIndex].quiz_date}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          font: {
            size: 11,
          },
          color: '#666',
          callback: function(value: any) {
            return value + '%';
          },
        },
        title: {
          display: true,
          text: 'Accuracy (%)',
          font: {
            size: 12,
            weight: 'bold' as const,
          },
          color: '#333',
        },
      },
      x: {
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          font: {
            size: 11,
          },
          color: '#666',
          maxRotation: 45,
        },
        title: {
          display: true,
          text: 'Quiz Date',
          font: {
            size: 12,
            weight: 'bold' as const,
          },
          color: '#333',
        },
      },
    },
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
  };

  return (
    <div className="card border-0 shadow-sm" style={{ borderRadius: '15px' }}>
      <div className="card-header bg-transparent border-0 pt-4">
        <h5 className="card-title fw-bold mb-0">
          <i className="fas fa-chart-line me-2 text-primary"></i>
          Quiz Progress Over Time
        </h5>
        <small className="text-muted">Track your accuracy improvement across recent quizzes</small>
      </div>
      <div className="card-body" style={{ height: '300px', width: '100%' }}>
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
};

export default QuizProgressChart;
